import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import cameraManager from '../../../viewer/camera-manager';
import {
  cardinalSides,
  CubeSide,
  cubeSides,
  getArrowOpacity,
  getArrowTransform,
  getCardinalPosition,
  getCubeTransform,
  getSideTransform,
  getSideViewQuaternion,
} from '../../../viewer/cameras/orientation-cube';
import { CameraType } from '../../../viewer/cameras/types';

const cubeSize = 56;
const arrow = { width: 14, length: 14, gap: 4 };
/** The area around an arrow that can be clicked, from the side out past its base. */
const arrowHitBox = { width: 28, length: arrow.gap + arrow.length + 6, gap: 0 };
const cardinalDistance = cubeSize;
const letterSize = 16;
/** Inside the arrow's box, with room for its outline. */
const arrowPoints = `${arrow.width / 2},1.5 ${arrow.width - 1.5},${arrow.length - 1.5} 1.5,${
  arrow.length - 1.5
}`;
/** How long the camera takes to turn to a side, in ms. */
const turnTime = 500;

/** Turns the camera to look straight at the side. */
function turnTo(side: CubeSide) {
  cameraManager().getActiveCamera()?.transformTo(undefined, getSideViewQuaternion(side), turnTime);
}

const isOrthographic = () =>
  cameraManager().getActiveCamera()?.cameraType === CameraType.OrthographicCamera;

/**
 * The orientation cube: turned as the camera sees the world, every frame, its sides turning the
 * camera to them when clicked. The arrows of the sides seen edge on, and the cardinal directions
 * beside them, flat on the screen, do so too, but not with the orthographic camera, which only
 * looks down.
 */
const OrientationCube = () => {
  const { t } = useTranslation();
  const cubeRef = useRef<HTMLDivElement>(null);
  const arrowRefs = useRef(new Map<CubeSide, HTMLElement[]>());
  const cardinalsRef = useRef<HTMLDivElement>(null);
  const cardinalRefs = useRef(new Map<keyof typeof cardinalSides, HTMLElement>());

  useEffect(() => {
    let frame = requestAnimationFrame(function update() {
      frame = requestAnimationFrame(update);
      const camera = cameraManager().getActiveCamera()?.getTHREECamera();
      if (!camera || !cubeRef.current) {
        return;
      }

      cubeRef.current.style.transform = getCubeTransform(camera);
      const orthographic = isOrthographic();
      const show = (element: HTMLElement, opacity: number) => {
        element.style.opacity = String(opacity);
        element.style.visibility = opacity > 0 ? 'visible' : 'hidden';
      };
      for (const [side, arrows] of arrowRefs.current) {
        const opacity = orthographic ? 0 : getArrowOpacity(side, camera);
        for (const element of arrows) {
          show(element, opacity);
        }
      }
      // Beside the arrows of their sides, and shown with them. Looking down orthographically, all
      // of them, around the cube like a compass.
      for (const [direction, element] of cardinalRefs.current) {
        const { x, y } = getCardinalPosition(direction, cardinalDistance, camera);
        // On whole pixels, or the letters are blurry
        element.style.transform = `translate(${Math.round(x - letterSize / 2)}px, ${Math.round(
          y - letterSize / 2,
        )}px)`;
        show(element, orthographic ? 1 : getArrowOpacity(cardinalSides[direction], camera));
      }
      cardinalsRef.current?.toggleAttribute('data-orthographic', orthographic);
    });
    return () => cancelAnimationFrame(frame);
  }, []);

  const sideLabels: Record<CubeSide, string> = {
    top: t('camera.orientationCube.sides.top', { ns: 'skyview' }),
    bottom: t('camera.orientationCube.sides.bottom', { ns: 'skyview' }),
    right: t('camera.orientationCube.sides.right', { ns: 'skyview' }),
    left: t('camera.orientationCube.sides.left', { ns: 'skyview' }),
    back: t('camera.orientationCube.sides.back', { ns: 'skyview' }),
    front: t('camera.orientationCube.sides.front', { ns: 'skyview' }),
  };

  const setArrowRef = (side: CubeSide, index: number) => (element: HTMLElement | null) => {
    const arrows = arrowRefs.current.get(side) ?? [];
    if (element) {
      arrows[index] = element;
    }
    arrowRefs.current.set(side, arrows);
  };

  return (
    <Component data-testid="orientation-cube">
      <Cube ref={cubeRef}>
        {cubeSides.map((side) => (
          <Side
            data-side={side}
            key={side}
            style={{ transform: getSideTransform(side, cubeSize) }}
            type="button"
            onClick={() => turnTo(side)}
          >
            {sideLabels[side]}
          </Side>
        ))}
        {cubeSides.flatMap((side) =>
          (['right', 'up'] as const).map((across, index) => (
            <Arrow
              aria-hidden={true}
              key={`${side}-${across}`}
              ref={setArrowRef(side, index)}
              style={{ transform: getArrowTransform(side, across, cubeSize, arrowHitBox) }}
              onClick={() => !isOrthographic() && turnTo(side)}
            >
              <svg
                height={arrow.length}
                viewBox={`0 0 ${arrow.width} ${arrow.length}`}
                width={arrow.width}
              >
                <polygon points={arrowPoints} />
              </svg>
            </Arrow>
          )),
        )}
      </Cube>
      <Cardinals ref={cardinalsRef}>
        {(Object.keys(cardinalSides) as (keyof typeof cardinalSides)[]).map((direction) => (
          <Cardinal
            key={direction}
            ref={(element) => {
              if (element) {
                cardinalRefs.current.set(direction, element);
              } else {
                cardinalRefs.current.delete(direction);
              }
            }}
            onClick={() => !isOrthographic() && turnTo(cardinalSides[direction])}
          >
            {direction}
          </Cardinal>
        ))}
      </Cardinals>
    </Component>
  );
};

const highlight = (props: { theme: { color: { brand: { dark: string } } } }) =>
  props.theme.color.brand.dark;

const outline = (color: string) =>
  [
    [-1, -1],
    [0, -1],
    [1, -1],
    [-1, 0],
    [1, 0],
    [-1, 1],
    [0, 1],
    [1, 1],
  ]
    .map(([x, y]) => `${x}px ${y}px 0 ${color}`)
    .join(', ');

// Room for the cardinal directions around the cube
const Component = styled.div`
  position: relative;
  width: ${2 * (cardinalDistance + letterSize)}px;
  height: ${2 * (cardinalDistance + letterSize)}px;
`;

// The world's origo at the middle, the cube around it
const Cube = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  width: 0;
  height: 0;
  transform-style: preserve-3d;
`;

const placed = `
  position: absolute;
  left: 0;
  top: 0;
  transform-origin: 0 0;
`;

const Side = styled.button`
  ${placed}
  width: ${cubeSize}px;
  height: ${cubeSize}px;
  padding: 2px;
  display: grid;
  place-items: center;

  font-family: 'Montserrat';
  font-size: 10px;
  font-weight: 600;
  overflow-wrap: anywhere;
  color: ${(props) => props.theme.color.gray.darkest};
  background-color: ${(props) => props.theme.color.gray.light};
  border: 1px solid ${(props) => props.theme.color.gray.darkest};
  backface-visibility: hidden;
  cursor: pointer;

  &:hover {
    background-color: ${highlight};
  }
`;

// A larger area than the arrow can be clicked. The arrow isn't cut out with clip-path, which
// Chrome doesn't draw when the arrow is seen from behind at a right angle, as after turning to a side.
const Arrow = styled.div`
  ${placed}
  width: ${arrowHitBox.width}px;
  height: ${arrowHitBox.length}px;
  display: flex;
  justify-content: center;
  padding-top: ${arrow.gap}px;
  box-sizing: border-box;
  cursor: pointer;

  // As the cube's sides, so they show on any background
  svg {
    display: block;
    fill: ${(props) => props.theme.color.gray.light};
    stroke: ${(props) => props.theme.color.gray.darkest};
    stroke-width: 1.5px;
    stroke-linejoin: round;
  }

  &:hover svg {
    fill: ${highlight};
  }
`;

// Flat on the screen, from the middle of the cube
const Cardinals = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;

  // Looking straight down, they don't turn the camera
  &[data-orthographic] > * {
    pointer-events: none;
  }
`;

const Cardinal = styled.div`
  ${placed}
  width: ${letterSize}px;
  height: ${letterSize}px;
  display: grid;
  place-items: center;

  font-family: 'Montserrat';
  font-size: 14px;
  font-weight: 700;
  // White with a sharp dark outline, as the icons below the cube, to show on any background
  color: #fff;
  text-shadow: ${(props) => outline(props.theme.color.gray.darkest)};
  cursor: pointer;
  user-select: none;

  &:hover {
    color: ${highlight};
  }
`;

export { OrientationCube };
